/**
 * `/email-templates` — View (and, from Slice 3, Edit) the branded email
 * templates the send path actually uses.
 *
 * The luxury HTML is generated by `@layer/email-templates`; this page renders
 * it through the same package (server-side, `GET /api/email-templates/:kind/preview`)
 * so what you see IS what a member receives. "View" opens the rendered email in
 * a sandboxed iframe — the same safe pattern as EmailHtmlModal (no scripts, no
 * same-origin; images + fonts still load).
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { EmailPreviewModal } from "../components/EmailPreviewModal";

interface TemplateMeta {
  readonly kind: string;
  readonly label: string;
  readonly category: "transactional" | "nurture" | "auth";
  readonly editable: boolean;
}

interface TemplatesResponse {
  readonly templates: ReadonlyArray<TemplateMeta>;
}

interface PreviewResponse {
  readonly kind: string;
  readonly html: string;
}

const CATEGORY_LABEL: Record<TemplateMeta["category"], string> = {
  transactional: "Transactional",
  nurture: "Nurture",
  auth: "Sign-in & verification",
};

const CATEGORY_ORDER: ReadonlyArray<TemplateMeta["category"]> = [
  "transactional",
  "nurture",
  "auth",
];

export function EmailTemplates(): JSX.Element {
  const [templates, setTemplates] = useState<ReadonlyArray<TemplateMeta> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ title: string; html: string } | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<TemplatesResponse>("/api/email-templates")
      .then((resp) => {
        if (cancelled) return;
        setTemplates(resp.templates);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError ? `api_error:${e.status}` : e.message,
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onView = useCallback(async (meta: TemplateMeta) => {
    setPreviewing(meta.kind);
    setError(null);
    try {
      const resp = await api.get<PreviewResponse>(
        `/api/email-templates/${meta.kind}/preview`,
      );
      setPreview({ title: meta.label, html: resp.html });
    } catch (e) {
      setError(
        e instanceof ApiError
          ? `preview_failed:${e.status}`
          : (e as Error).message,
      );
    } finally {
      setPreviewing(null);
    }
  }, []);

  return (
    <div>
      <h1 className="page-title">Email templates</h1>
      <p className="dc-muted" style={{ maxWidth: "640px" }}>
        The branded HTML emails the automations actually send. Preview any one
        exactly as a member receives it. Transactional templates can be edited;
        the nurture shell and sign-in emails are view-only for now.
      </p>

      {loading ? <p className="dc-muted">Loading…</p> : null}
      {error !== null ? <p className="action-modal-error">{error}</p> : null}

      {templates !== null
        ? CATEGORY_ORDER.map((cat) => {
            const rows = templates.filter((t) => t.category === cat);
            if (rows.length === 0) return null;
            return (
              <section key={cat} style={{ marginTop: "22px" }}>
                <h2 style={{ fontSize: "16px", margin: "0 0 8px" }}>
                  {CATEGORY_LABEL[cat]}
                </h2>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Template</th>
                      <th>Kind</th>
                      <th style={{ width: "180px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => (
                      <tr key={t.kind}>
                        <td>{t.label}</td>
                        <td>
                          <code>{t.kind}</code>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="row-action-btn"
                            disabled={previewing === t.kind}
                            onClick={() => void onView(t)}
                          >
                            {previewing === t.kind ? "Loading…" : "View"}
                          </button>
                          {t.editable ? (
                            <a
                              className="row-action-btn"
                              style={{ marginLeft: "8px" }}
                              href={`/email-templates/${t.kind}/edit`}
                            >
                              Edit
                            </a>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            );
          })
        : null}

      {preview !== null ? (
        <EmailPreviewModal
          title={preview.title}
          subtitle="Rendered with sample values — exactly how the email is built for a member."
          html={preview.html}
          onClose={() => setPreview(null)}
        />
      ) : null}
    </div>
  );
}
